import { OrderModel } from "../../../screens/portfolio-info/types/OrderModel";
import { AssetType } from "../../types/AssetType";
import { blackScholesModelCalculationService } from "../BlackScholesModelCalculationService";
import { PositionModel } from "../../../screens/portfolio-info/types/PositionModel";
import { PortfolioGroupedItem } from "../../../screens/positions-and-orders/models/PortfolioGroupedItem";
import { OrderState } from "../../types/OrderState";

export class BalanceCalculationService {
    calculateAtCurrent(orders: OrderModel[], underlyingPrice: number): number {
        let balance = 0;

        orders.map(order => {
            if (this.isEnabled(order)){
                balance += this.calculate(order, underlyingPrice, new Date());
            }
        });

        return balance;
    }

    calculateAtCustom(orders: OrderModel[], underlyingPrice: number, customDate: Date): number {
        let balance = 0;

        orders.map(order => {
            if (this.isEnabled(order))
                balance += this.calculate(order, underlyingPrice, customDate);
        });

        return balance;
    }
    
    calculateAtExpiration(orders: OrderModel[], underlyingPrice: number): number {
        let balance = 0;

        orders.map(order => {
            if (this.isEnabled(order))
                balance += this.calculateBalanceAtExpiration(order, underlyingPrice);
        });

        return balance;
    }
    
    calculate(order: OrderModel, underlyingPrice: number, fromDate: Date): number {
        if (order.type === AssetType.Futures || order.type === AssetType.Spot){
            return (underlyingPrice - order.openPrice) * order.size;
        }
        else if (order.type === AssetType.Call || order.type === AssetType.Put) {
            if (order.expireAt === null) {
                throw new Error("Options expiration date is null")
            }
            
            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(order.expireAt) < 0;
            
            const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(order.expireAt, fromDate);
            
            return isExpired || timeToExpirationInShareOfYear < 0
                ? this.calculateBalanceAtExpiration(order, underlyingPrice)
                : (this.calculateOptionMarkPrice(order, underlyingPrice, timeToExpirationInShareOfYear) - order.openPrice) * order.size;
        }

        throw new Error("Unknown AssetType");
    }
    
    calculateOptionMarkPrice(order: OrderModel,
        underlyingPrice: number, timeToExpirationInShareOfYear: number
    ): number {
        if (!order.markImpliedVolatility) {
            throw new Error("ImpliedVolatility cannot be null!");
        }

        if (!order.strike) {
            throw new Error("Order property 'strike' is null.")
        }

        return blackScholesModelCalculationService.getPremium(
            order.type, underlyingPrice, order.strike,
            timeToExpirationInShareOfYear, order.markImpliedVolatility);
    }

    calculateGroupedPositionBalanceAfterExpiration(position: PortfolioGroupedItem): number {
        if (position.type !== AssetType.Call && position.type !== AssetType.Put) {
            throw new Error("Items must be only option")
        }

        if (position.expireAt === null) {
            throw new Error("Options expiry date is null")
        }

        if (position.expirationPrice === null) {
            throw new Error("Options expiry price is null")
        }

        if (position.strike === null) {
            throw new Error("Options strike is null")
        }

        const value = position.type === AssetType.Call
            ? position.strike < position.expirationPrice
                ? (position.expirationPrice - position.strike - position.averageOpenPrice!)
                : - position.averageOpenPrice!
            : position.strike > position.expirationPrice
                ? (position.strike - position.expirationPrice - position.averageOpenPrice!)
                : - position.averageOpenPrice!;

        return value * position.currentSize;
    }
    
    calculatePositionBalanceAfterExpiration(position: PositionModel): number {
        if (position.type !== AssetType.Call && position.type !== AssetType.Put) {
            throw new Error("Items must be only option")
        }

        if (position.expireAt === null) {
            throw new Error("Options expiry date is null")
        }

        if (position.expirationPrice === null) {
            throw new Error("Options expiry price is null")
            }

        if (position.strike === null) {
            throw new Error("Options strike is null")
        }

        return position.type === AssetType.Call
            ? position.strike < position.expirationPrice
                ? position.volume + (position.expirationPrice - position.strike) * position.size
                : position.volume
            : position.strike > position.expirationPrice
                ? position.volume + (position.strike - position.expirationPrice) * position.size
                : position.volume;
    }
    
    calculateOrderBalanceAfterExpiration(order: OrderModel): number {
        if (order.type !== AssetType.Call && order.type !== AssetType.Put) {
            throw new Error("Items must be only option")
        }

        if (order.expireAt === null) {
            throw new Error("Options expiry date is null")
        }

        if (order.expirationPrice === null) {
            throw new Error("Options expiry price is null")
        }

        if (order.strike === null) {
            throw new Error("Options strike is null")
        }

        const value = order.type === AssetType.Call
            ? order.strike < order.expirationPrice
                ? (order.expirationPrice - order.strike - order.openPrice)
                : - order.openPrice
            : order.strike > order.expirationPrice
                ? (order.strike - order.expirationPrice - order.openPrice)
                : - order.openPrice;
        
        return value * order.size;
    }
    
    private calculateBalanceAtExpiration(order: OrderModel, underlyingPrice: number): number {
        if (order.type === AssetType.Call) {
            if (order.strike === null) {
                throw new Error("Order property 'strike' is null.")
            }

            if (order.expireAt === null)
                throw new Error("Options expiration date is null.")

            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(order.expireAt) < 0;

            return !isExpired
                ? order.strike < underlyingPrice
                    ? (underlyingPrice - order.strike) * order.size - order.openPrice * order.size
                    : - order.openPrice * order.size
                : this.calculateOrderBalanceAfterExpiration(order);
        }
        else if (order.type === AssetType.Put) {
            if (order.strike === null) {
                throw new Error("Order property 'strike' is null.")
            }

            if (order.expireAt === null)
                throw new Error("Options expiration date is null.")

            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(order.expireAt) < 0;

            return !isExpired
                ? order.strike > underlyingPrice
                    ? (order.strike - underlyingPrice) * order.size - order.openPrice * order.size
                    : - order.openPrice * order.size
                : this.calculateOrderBalanceAfterExpiration(order);
        }
        else if (order.type === AssetType.Futures || order.type === AssetType.Spot) {
            return (underlyingPrice - order.openPrice) * order.size;
        }

        throw new Error("Unknown AssetType");
    }

    private isEnabled(order: OrderModel): boolean {
        return order.state !== OrderState.Disabled;
    }
}
